/**
 * <pdp-sport-lens>
 *
 * Carrossel de lentes para a PDP Sport.
 * Idêntico ao sport-lens-carousel da LP, exceto pelo alinhamento:
 * exibe 2 itens lado a lado (alinhados à esquerda), sem item centralizado
 * com meios itens visíveis nas bordas.
 *
 * Única diferença em relação ao sport-lens-carousel:
 *   _indexToX → sem centerOffset, alinhado ao início.
 */
(function () {
  if (customElements.get('pdp-sport-lens')) return;

  class PdpSportLens extends HTMLElement {
    constructor() {
      super();
      /** @type {HTMLElement|null}  */ this._track      = null;
      /** @type {HTMLElement|null}  */ this._btnNext    = null;
      /** @type {number} */           this._index      = 0;
      /** @type {number} */           this._itemW      = 0;
      /** @type {number} */           this._realN      = 0;
      /** @type {boolean} */          this._dragging   = false;
      /** @type {number} */           this._dragStartX = 0;
      /** @type {number} */           this._baseX      = 0;
      /** @type {number|null} */      this._resizeTimer = null;
      /** @type {(() => void)|null} */ this._onResize  = null;
    }

    connectedCallback() {
      this._track   = /** @type {HTMLElement|null} */ (this.querySelector('[data-lens-track]'));
      this._btnNext = /** @type {HTMLElement|null} */ (this.querySelector('[data-lens-next]'));
      if (!this._track) return;

      this._buildClones();
      this._measure();
      this._bindEvents();

      this._onResize = () => {
        clearTimeout(this._resizeTimer ?? 0);
        this._resizeTimer = setTimeout(() => this._measure(), 300);
      };
      window.addEventListener('orientationchange', this._onResize);
    }

    disconnectedCallback() {
      if (this._onResize) window.removeEventListener('orientationchange', this._onResize);
    }

    // ─── Clones ────────────────────────────────────────────────────────────

    _buildClones() {
      const track = /** @type {HTMLElement} */ (this._track);
      const real  = Array.from(track.children);
      this._realN = real.length;

      const before = document.createDocumentFragment();
      const after  = document.createDocumentFragment();

      real.forEach(item => {
        const b = /** @type {HTMLElement} */ (item.cloneNode(true));
        const a = /** @type {HTMLElement} */ (item.cloneNode(true));
        b.setAttribute('aria-hidden', 'true');
        a.setAttribute('aria-hidden', 'true');
        before.appendChild(b);
        after.appendChild(a);
      });

      const first = real[0];
      if (first) track.insertBefore(before, first);
      track.appendChild(after);
    }

    // ─── Medição ───────────────────────────────────────────────────────────

    _measure() {
      const track = /** @type {HTMLElement} */ (this._track);
      const items = /** @type {HTMLElement[]} */ (Array.from(track.children));
      this._itemW  = items[this._realN]?.offsetWidth ?? 0;
      this._index  = this._realN;
      this._baseX  = this._indexToX(this._realN);
      this._setTransform(this._baseX, false);
    }

    // ─── Posição: alinhado à esquerda (sem centralização) ──────────────────

    /** @param {number} index */
    _indexToX(index) {
      return -index * this._itemW;
    }

    // ─── Navegação ─────────────────────────────────────────────────────────

    _advance() { this._index++; this._animateTo(this._index); }
    _retreat() { this._index--; this._animateTo(this._index); }

    /** @param {number} index */
    _animateTo(index) {
      const track = /** @type {HTMLElement} */ (this._track);
      this._setTransform(this._indexToX(index), true);

      track.addEventListener('transitionend', () => {
        const N = this._realN;
        if (this._index < N) {
          this._index += N;
        } else if (this._index >= 2 * N) {
          this._index -= N;
        }
        this._baseX = this._indexToX(this._index);
        requestAnimationFrame(() => {
          track.style.transition = 'none';
          requestAnimationFrame(() => {
            track.style.transform = `translateX(${this._baseX}px)`;
          });
        });
      }, { once: true });
    }

    // ─── Drag ──────────────────────────────────────────────────────────────

    /** @param {number} x */
    _dragStart(x) {
      this._dragging   = true;
      this._dragStartX = x;
      this._track?.classList.add('is-dragging');
    }

    /** @param {number} x */
    _dragMove(x) {
      if (!this._dragging) return;
      this._setTransform(this._baseX + (x - this._dragStartX), false);
    }

    /** @param {number} x */
    _dragEnd(x) {
      if (!this._dragging) return;
      this._dragging = false;
      this._track?.classList.remove('is-dragging');

      const diff      = x - this._dragStartX;
      const threshold = this._itemW * 0.2;

      if      (diff < -threshold) { this._index++; this._animateTo(this._index); }
      else if (diff >  threshold) { this._index--; this._animateTo(this._index); }
      else                        { this._setTransform(this._baseX, true); }
    }

    // ─── Eventos ───────────────────────────────────────────────────────────

    _bindEvents() {
      if (this._btnNext) {
        this._btnNext.addEventListener('click', () => this._advance());
      }

      const track = /** @type {HTMLElement} */ (this._track);
      let touchTime = 0;
      const isSyntheticMouse = () => Date.now() - touchTime < 500;

      track.addEventListener('touchstart', e => {
        touchTime = Date.now();
        const t = e.touches[0];
        if (t) this._dragStart(t.clientX);
      }, { passive: true });

      track.addEventListener('touchmove', e => {
        const t = e.touches[0];
        if (t) this._dragMove(t.clientX);
      }, { passive: true });

      track.addEventListener('touchend', e => {
        touchTime = Date.now();
        const t = e.changedTouches[0];
        if (t) this._dragEnd(t.clientX);
      });

      track.addEventListener('mousedown', e => {
        if (isSyntheticMouse()) return;
        e.preventDefault();
        this._dragStart(e.pageX);
      });
      track.addEventListener('mousemove', e => {
        if (isSyntheticMouse()) return;
        this._dragMove(e.pageX);
      });
      track.addEventListener('mouseup', e => {
        if (isSyntheticMouse()) return;
        this._dragEnd(e.pageX);
      });
      track.addEventListener('mouseleave', e => {
        if (isSyntheticMouse()) return;
        this._dragEnd(e.pageX);
      });
    }

    // ─── Render ────────────────────────────────────────────────────────────

    /**
     * @param {number}  x
     * @param {boolean} animated
     */
    _setTransform(x, animated) {
      if (!this._track) return;
      this._track.style.transition = animated ? 'transform 0.35s ease' : 'none';
      this._track.style.transform  = `translateX(${x}px)`;
    }
  }

  customElements.define('pdp-sport-lens', PdpSportLens);
})();
